@if (groupsError()) {
  <ng-container *ngTemplateOutlet="loadError; context: { $implicit: null }"></ng-container>
}

@for (group of groups(); track group.key) {
  @let blockState = loadStates()[group.key];
  @let codesOfBlock = blockCodes(group);
  <div class="flex w-full items-center gap-2 border-b border-neutral-100 bg-neutral-50 px-3.5 py-2">
    @if (action()) {
      <ng-container *ngTemplateOutlet="checkbox; context: { $implicit: codesOfBlock, block: group }"></ng-container>
    }
    <button
      class="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
      type="button"
      (click)="toggleBlock(group)"
    >
      <mat-icon class="mt-0.5 shrink-0 text-center text-sm text-neutral-400">{{ isBlockExpanded(group.key) ? "expand_more" : "chevron_right" }}</mat-icon>
      <span class="w-16 shrink-0 font-bold text-neutral-500">{{ group.key }}</span>
      <span class="min-w-0 flex-1 truncate font-bold text-neutral-500">{{ names()[group.key] }}</span>
      @if (blockState?.loading) {
        <mat-spinner diameter="16"></mat-spinner>
      }
      @if (group.count) {
        <insurance-packages-diagnoses-count-badge
          [count]="group.count"
          [showIcon]="false"
        ></insurance-packages-diagnoses-count-badge>
      }
    </button>
    <ng-container *ngTemplateOutlet="nodeAction; context: { $implicit: group.key, codes: codesOfBlock }"></ng-container>
  </div>

  @if (isBlockExpanded(group.key)) {
    @if (blockState?.error) {
      <ng-container *ngTemplateOutlet="loadError; context: { $implicit: group.key }"></ng-container>
    }

    @for (item of group.categories; track item.category) {
      @let categoryState = loadStates()[item.category];
      <div class="flex w-full items-center gap-2 border-b border-neutral-100 py-2 pr-3.5 pl-11 hover:bg-primary-50">
        @if (action()) {
          <ng-container *ngTemplateOutlet="checkbox; context: { $implicit: item.codes, category: item.category }"></ng-container>
        }
        <button
          class="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
          type="button"
          (click)="toggleCategory(item.category)"
        >
          <mat-icon class="mt-0.5 shrink-0 text-center text-sm text-neutral-400">{{ isCategoryExpanded(item.category) ? "expand_more" : "chevron_right" }}</mat-icon>
          <span class="w-10 shrink-0 font-bold text-neutral-500">{{ item.category }}</span>
          <span class="min-w-0 flex-1 truncate text-neutral-400">{{ names()[item.category] }}</span>
          <span class="shrink-0 text-xs text-neutral-300">{{ item.codes.length }}</span>
        </button>
        <ng-container *ngTemplateOutlet="nodeAction; context: { $implicit: item.category, codes: item.codes }"></ng-container>
      </div>

      @if (isCategoryExpanded(item.category)) {
        @if (categoryState?.loading) {
          <div class="flex justify-center border-b border-neutral-100 py-3">
            <mat-spinner diameter="20"></mat-spinner>
          </div>
        } @else if (categoryState?.error) {
          <ng-container *ngTemplateOutlet="loadError; context: { $implicit: item.category }"></ng-container>
        } @else {
          @for (code of item.codes; track code) {
            <insurance-packages-icd10-code-row
              class="pl-20"
              [code]="code"
              [name]="names()[code] ?? ''"
              [action]="action()"
              [selected]="selectedCodes().has(code)"
              [actionable]="showAction()"
              [present]="presentCodes().has(code)"
              (selectedToggle)="toggleSelection([code])"
              (remove)="remove.emit([code])"
              (add)="add.emit(nodeEntry(code)!)"
            ></insurance-packages-icd10-code-row>
          }
        }
      }
    }
  }
} @empty {
  <p class="px-3.5 py-3 text-neutral-300">{{ "packageConfigurator.message.noDiagnoses" | translate }}</p>
}

<ng-template
  #checkbox
  let-codes
  let-block="block"
  let-category="category"
>
  @let state = selection(codes);
  <input
    class="h-4 w-4 shrink-0 cursor-pointer accent-primary-600 disabled:cursor-default disabled:opacity-40"
    type="checkbox"
    [attr.aria-label]="'packageConfigurator.action.selectDiagnoses' | translate"
    [checked]="state === 'all'"
    [indeterminate]="state === 'some'"
    [disabled]="!selectable(codes).length"
    (change)="toggleNodeSelection(codes, block, category)"
  />
</ng-template>

<ng-template
  #nodeAction
  let-key
  let-codes="codes"
>
  @if (action() === "add") {
    @let listed = presentUnder(key);
    @if (listed) {
      <span class="shrink-0 rounded-full bg-success-50 px-2 py-0.5 text-xs font-semibold text-success-700">{{
        "packageConfigurator.message.diagnosesInList" | translate: { count: listed }
      }}</span>
    }
    @if (showAction() && nodeEntry(key); as entry) {
      <button
        class="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-primary-200 bg-primary-50 text-primary-700"
        type="button"
        [matTooltip]="'packageConfigurator.action.addDiagnosesGroup' | translate"
        (click)="add.emit(entry)"
      >
        <mat-icon class="mt-1 text-sm">add</mat-icon>
      </button>
    }
  } @else if (action() === "remove" && showAction()) {
    <button
      class="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-error-200 bg-error-50 text-error-700"
      type="button"
      [matTooltip]="'packageConfigurator.action.removeDiagnosesGroup' | translate: { count: codes.length }"
      (click)="remove.emit(codes)"
    >
      <mat-icon class="mt-1 text-sm">delete</mat-icon>
    </button>
  }
</ng-template>

<ng-template
  #loadError
  let-key
>
  <div class="flex items-center gap-2 border-b border-neutral-100 py-2 pr-3.5 pl-11">
    <span class="text-error-500">{{ "packageConfigurator.message.diagnosisNamesLoadError" | translate }}</span>
    <button
      class="cursor-pointer font-bold text-primary-500 hover:text-primary-600"
      type="button"
      (click)="key === null ? retryGroups.emit() : retry.emit(key)"
    >
      {{ "packageConfigurator.action.retry" | translate }}
    </button>
  </div>
</ng-template>
